import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { gridToWorld } from '../facilityCoords';
import { cornerPillarOffsets } from '../buildingGeometry';
import { scaffoldUprights } from '../buildingDetails';

const SCAFFOLD_H = 1.4;
const SCAFFOLD_T = 0.05;
const CAUTION_COLORS = ['#d4a017', '#1a1a1a'];

/**
 * Locked room rendered as a construction site: slab + rebar stubs + partial wall.
 * @param {object} roomDef - FACILITY_ROOMS entry (locked, non-ghost)
 * @param {function} [onClick] - called when player clicks to unlock
 */
export function ConstructionSite({ roomDef, onClick }) {
  const lightRef = useRef();
  const { x, z, boxW, boxD } = gridToWorld(roomDef);
  const pillars = cornerPillarOffsets(boxW, boxD, 0.15);
  const uprights = scaffoldUprights(boxW, boxD);
  const scaffoldPairs = [
    [0, 1],
    [1, 3],
    [3, 2],
    [2, 0],
  ];
  const crossbarHeights = [0.35, 0.75, 1.05];
  const cautionSegments = 10;
  const segmentW = boxW / cautionSegments;

  useFrame(({ clock }) => {
    const on = Math.sin(clock.elapsedTime * 3) > 0;
    if (lightRef.current) lightRef.current.emissiveIntensity = on ? 1.2 : 0.05;
  });

  const handlers = onClick
    ? {
        onClick: (e) => { e.stopPropagation(); onClick?.(); },
        onPointerEnter: (e) => { e.stopPropagation(); document.body.style.cursor = 'pointer'; },
        onPointerLeave: () => { document.body.style.cursor = 'default'; },
      }
    : {};

  return (
    <group position={[x, 0, z]} {...handlers}>
      {/* Concrete slab */}
      <mesh position={[0, 0.06, 0]} receiveShadow>
        <boxGeometry args={[boxW, 0.12, boxD]} />
        <meshStandardMaterial color="#161c1a" emissive="#1a2018" emissiveIntensity={0.05} roughness={1} metalness={0} />
      </mesh>

      {/* Rebar stub pillars */}
      {pillars.map(([dx, dz], i) => (
        <mesh key={i} position={[dx, 0.45, dz]} castShadow>
          <cylinderGeometry args={[0.04, 0.04, 0.8, 6]} />
          <meshStandardMaterial color="#3a2f1a" emissive="#d29922" emissiveIntensity={0.08} roughness={0.8} metalness={0.5} />
        </mesh>
      ))}

      {/* Low partial wall (back edge) */}
      <mesh position={[0, 0.28, -boxD / 2 + 0.1]} castShadow>
        <boxGeometry args={[boxW * 0.8, 0.44, 0.12]} />
        <meshStandardMaterial color="#23282b" roughness={0.95} metalness={0.05} />
      </mesh>

      {/* Scaffold uprights */}
      {uprights.map(([dx, dz], i) => (
        <mesh key={`scaffold-${i}`} position={[dx, SCAFFOLD_H / 2, dz]} castShadow>
          <boxGeometry args={[SCAFFOLD_T, SCAFFOLD_H, SCAFFOLD_T]} />
          <meshStandardMaterial color="#5a6068" roughness={0.7} metalness={0.35} />
        </mesh>
      ))}

      {/* Scaffold crossbars between adjacent uprights */}
      {scaffoldPairs.map(([a, b], pairIdx) => {
        const [ax, az] = uprights[a];
        const [bx, bz] = uprights[b];
        const midX = (ax + bx) / 2;
        const midZ = (az + bz) / 2;
        const spanX = Math.abs(bx - ax);
        const spanZ = Math.abs(bz - az);
        return crossbarHeights.map((h, hi) => (
          <mesh key={`cross-${pairIdx}-${hi}`} position={[midX, h, midZ]} castShadow>
            <boxGeometry args={[spanX + SCAFFOLD_T, SCAFFOLD_T, spanZ + SCAFFOLD_T]} />
            <meshStandardMaterial color="#6a7078" roughness={0.65} metalness={0.4} />
          </mesh>
        ));
      })}

      {/* Caution stripe along +Z slab edge */}
      {Array.from({ length: cautionSegments }, (_, i) => {
        const segX = -boxW / 2 + segmentW * (i + 0.5);
        return (
          <mesh key={`caution-${i}`} position={[segX, 0.125, boxD / 2 + 0.02]}>
            <boxGeometry args={[segmentW * 0.92, 0.04, 0.06]} />
            <meshStandardMaterial color={CAUTION_COLORS[i % 2]} roughness={0.8} metalness={0.1} />
          </mesh>
        );
      })}

      {/* Tarp / plywood panel tilted on one side */}
      <mesh
        position={[-boxW / 2 + 0.18, 0.55, boxD * 0.15]}
        rotation={[0, 0.15, 0.35]}
        castShadow
      >
        <boxGeometry args={[0.55, 0.9, 0.04]} />
        <meshStandardMaterial color="#4a3a28" roughness={0.95} metalness={0.05} />
      </mesh>

      {/* Work light pole at front corner */}
      <mesh position={[boxW / 2 - 0.15, 0.55, boxD / 2 - 0.15]} castShadow>
        <cylinderGeometry args={[0.025, 0.025, 1.1, 6]} />
        <meshStandardMaterial color="#3a4045" roughness={0.6} metalness={0.5} />
      </mesh>
      <mesh position={[boxW / 2 - 0.15, 1.12, boxD / 2 - 0.15]}>
        <boxGeometry args={[0.12, 0.08, 0.1]} />
        <meshStandardMaterial
          ref={lightRef}
          color="#ffe066"
          emissive="#ffe066"
          emissiveIntensity={1.2}
          roughness={0.3}
          metalness={0.2}
        />
      </mesh>
    </group>
  );
}
